// 月曆格的週期視覺（W58.1）—— **這個檔案只匯出元件**。
//
// Vite 的 fast refresh 要求「一個檔案只匯出元件」，所以純函式
// （`resolveCycleState` / `hasCycleVisual` / `cycleVisualKind` /
// `cycleWash`）在 `cycleState.ts`。它們本來在這裡，而那帶來了三條 dev
// server 警告。
//
//
// **一個通道講一件事**（root `DESIGN §4.1c`）：
//
//   - **chip** = 事件（別的模組）
//   - **角標** = 週期的狀態（日期數字右上角的那朵花）
//   - **底色** = 週期的狀態（這幾天是什麼）
//
// W58.0 的月曆格只有一條 rail，而且只分得出「推斷帶 / 沒有」。Meetyou 的
// 月曆分得出四種：真實經期（紅）、預測經期（淡紅）、排卵期（紫）、安全期
// （白）。這一輪把那四種補上。
//
// ---
//
// **marker 為什麼在 chip 行，而不在格子的右上角。**
//
// 實測（`scripts/shoot-w58-cycle-live.mjs`）：badge 放在右上角時，**320px
// 的格子上它撞到日期數字 5.4px** —— 320px 的格子只有 37.7px 寬，日期數字
// 是置中的，兩個角上放不下東西。chip 行是**固定高度**的（沒有事件也照樣
// 渲染），所以那裡永遠有一個位置。
//
// **W58.5 —— 那個結論被推翻了，而推翻它的理由更強。**
//
// 上一條講的是「放在**格子的**右上角」—— 那確實放不下。但使用者要的不是
// 格子的角，是**日期數字的角**：「日期數字右上角的微型小花角標」。而**日期
// 數字是置中的**，所以角標若**絕對定位在數字自己身上**（而不是格子上），
// 它就只跟數字搶位置，不跟格子搶位置 —— 320px 的格子上仍然放得下：
//
// ```text
//   ┌─────────┐
//   │  12  🌸 │  ← 角標絕對定位在「數字」這個 inline-flex 上
//   │ ⚑ ⚑    │  ← row A：兩個**事件** chip（100% 留給事件）
//   │ ⚑  +2   │  ← row B：一個事件 chip + 溢位
//   └─────────┘
// ```
//
// **而這一輪真正的理由不是「放不放得下」，是「那一格下半部是誰的」。**
// 事件槽位 **100% 留給雙人事件與其他模組的事件** —— 那是月曆格存在的理由。
// 週期改成一朵角標 + 一層底色之後，**一個事件的槽位都沒有被佔走**。
//
// **實心 / 空心講狀態，顏色講階段家族。**
//
//   - 實際經期 → 實心玫瑰花
//   - 預測經期 → 空心玫瑰花
//   - 排卵日   → 實心紫花
//   - 排卵期   → 空心紫花
//
// **為什麼不保留「誰的經期」在格子上的區分。** 既有的 chip 用「填色 = 你、
// 外框 = TA」講歸屬（`DESIGN §3.1`）。而這一輪要的是「實心 = 真的、空心 =
// 推算」。兩者都是「實心 / 空心」，放在一起會打架。於是**格子講狀態**，
// **誰的經期由日詳情與週期頁的歷史清單講** —— 那兩個地方本來就有歸屬。
//
// ---
//
// **W58.3 —— 底部那條線拿掉了，而「連續」改由每格的底色承擔。**
//
// 實測反饋：「**下個月預測和排卵期和易孕期都還有一條顏色線**」。
//
// 底色升格成唯一的狀態通道（W58.2）之後，底色**下面**再壓一根線就是
// 冗餘 —— 而且那根線講的資訊（這是一段連續的區間）已經沒有消費者：
// 連續性要靠**跨格橋接**才畫得出來，而橋接在一個 9px 圓角 + 4px 格間距
// 的格子上面必然留缺口（圓角與格間距互相打架）。
//
// 所以這一輪的取捨是：**寧可每格各自填色，不要一根講不清的線。** 五天
// 的經期是五格相鄰的淡粉 —— 連續性由「它們挨在一起」講，不靠一根線。
// 這也是 Meetyou 的做法：經期日是整格填色，月曆格之間留縫。

import { CycleFlower, CycleFlowerHollow } from "@/components/calendar/CycleFlower";
import { cn } from "@/lib/utils/cn";
import type { CycleVisualKind } from "@/components/calendar/cycleState";

/**
 * The badge — the flower at the top-right of the date number.
 *
 * **它取代了週期事件的 chip。** 實際記錄的經期如果同時畫一個事件 chip
 * 與一個角標，那一朵花會出現兩次（而且兩個的顏色語言不同）。角標
 * 就是那個事件的**格子上的樣子** —— 日詳情與週期頁的歷史清單仍然列得出
 * 它的標題與歸屬。
 *
 * **易孕期沒有角標。** 它在格子上只有底色，而四個角標已經是格子的
 * 上限（再多就會跟事件 chip 擠在一起）。
 *
 * **實心 / 空心是這條通道。** `actual` 與 `predicted` 的**底色**是 25% 與
 * 15% 的玫瑰（很接近），`ovulation-day` 與 `ovulation` 的底色**完全相同**
 * —— 分開它們的只有實心 / 空心。圓盤沒了（角標只有一朵花），所以這條
 * 通道由**花本身**講。
 *
 * **W58.5 —— `kind` 由呼叫端算好傳進來。** 這不是為了少傳一個參數，是
 * 為了讓**圖例能畫出同一朵花**：圖例那一格裡的花，與月曆格上的花，
 * 必須是**同一個元件**畫出來的，否則「圖例的 icon 和實際不一致」這種
 * 反饋會再出現一次。
 */
export function CycleBadge({
  kind,
  size = 10,
  className,
}: {
  kind: CycleVisualKind | null;
  /** 角標的邊長（px）。10 是月曆格上的尺寸。 */
  size?: number;
  className?: string;
}) {
  if (!kind || kind === "fertile") return null;

  // 實心 = 事實（真的經期 / 真的排卵日）；空心 = 推算或區間。
  const filled = kind === "actual" || kind === "ovulation-day";
  const Icon = filled ? CycleFlower : CycleFlowerHollow;

  return (
    <Icon
      size={size}
      aria-hidden="true"
      className={cn(
        // 玫瑰講經期，紫講受孕 —— 顏色講**階段家族**，實心/空心講**狀態**。
        kind === "actual" || kind === "predicted"
          ? "text-partner-strong"
          : "text-shared-strong",
        className,
      )}
      data-testid={`calendar-cycle-badge-${kind}`}
    />
  );
}
